import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useBranding } from "../theme/BrandingContext";
import {
  LayoutDashboard,
  Users,
  Clock,
  Calendar,
  LogOut,
  Menu,
  Settings,
  ChevronRight,
  Navigation,
  Megaphone,
  MessageSquare,
  Monitor,
  Tv,
  Image as ImageIcon,
  X,
  Bell,
  Building2,
  Shield,
  ShieldCheck,
  CalendarDays,
  CalendarClock,
  Smartphone,
  Plug,
  BarChart3,
  Wallet,
  Package,
  FileText,
  Mail,
  HardDrive,
  Activity as ActivityIcon,
  Keyboard,
  TrendingUp,
  Power,
  Briefcase,
  LifeBuoy,
  ReceiptText,
  Award,
  Cctv,
  RadioTower,
} from "lucide-react";
import { usePermission, useAnyPermission } from "../hooks/usePermission";
import { useModules } from "../theme/ModuleContext";

/**
 * Sidebar — three-mode responsive nav shell.
 *
 *   • Desktop expanded  (≥ md, ~256 px)
 *   • Desktop collapsed (≥ md, ~80 px)   — icon rail with hover tooltips
 *   • Mobile drawer     (<  md)          — slides in over content w/ backdrop
 *
 * Animations are all CSS transitions; no extra libs.
 *   - Width:   transition-[width]         on the shell
 *   - Label:   opacity + translate-x fade on each <Label/>
 *   - Submenu: grid-template-rows 0fr ↔ 1fr (smooth height w/o JS measuring)
 *   - Arrow:   rotate-90 on the chevron
 *   - Active:  left vertical bar slides in from the edge
 *
 * Props:
 *   collapsed      boolean   desktop collapsed state
 *   setCollapsed   fn        toggler
 *   mobileOpen     boolean   drawer open on mobile
 *   setMobileOpen  fn        drawer toggler
 *   isMobile       boolean   from useMediaQuery
 *   onLogout       fn        logout handler
 */
const WIDTH_EXPANDED = "w-64";
const WIDTH_COLLAPSED = "w-20";
const WIDTH_MOBILE = "w-72";

export default function Sidebar({
  collapsed,
  setCollapsed,
  mobileOpen,
  setMobileOpen,
  isMobile,
  onLogout,
}) {
  const location = useLocation();
  const scrollerRef = useRef(null);
  const branding = useBranding();
  const { isModuleEnabled, modules } = useModules();

  // ── RBAC: hide nav entries the caller can't act on. ADMIN bypasses every
  // check inside usePermission, so the rail looks identical for them.
  // We hide entries instead of disabling them so collapsed-rail tooltips
  // don't promise actions that 403 on click.
  const canSeeUsers           = usePermission("users.view") && isModuleEnabled("employees");
  const canSeeLeaveBalances   = usePermission("leaves.balance.write") && isModuleEnabled("leaves");
  const canSeePayroll          = usePermission("payroll.view") && isModuleEnabled("payroll");
  // Either permission opens the page — shifts.manage implies shifts.view.
  // Variadic, not an array — useAnyPermission(...keys). Passing an array makes
  // it test a single key that is itself an array, which is always false.
  const canSeeShifts           = useAnyPermission("shifts.view", "shifts.manage")
                                  && isModuleEnabled("attendance");
  const canSeeMobileReleases   = usePermission("mobile.release.manage");
  const hasTravelPermission   = useAnyPermission("travel.view", "travel.approve");
  const canSeeTravel          = hasTravelPermission && isModuleEnabled("travel");
  const canSeeClients         = usePermission("clients.view");
  const hasReimbursementPermission = useAnyPermission(
    "reimbursements.view_own", "reimbursements.view", "reimbursements.view_all",
    "reimbursements.create_own", "reimbursements.create",
    "reimbursements.approve", "reimbursements.finance"
  );
  const canSeeReimbursements = hasReimbursementPermission && isModuleEnabled("reimbursements");
  const canSeeAnnouncements   = usePermission("announcements.view") && isModuleEnabled("announcements");
  // Not module-gated: customer feedback has no entry in the module registry,
  // so gate on the permission alone rather than hiding it for everyone.
  const canSeeFeedback        = usePermission("feedback.view");
  const canSeeCertificates    = usePermission("certificate.view");
  // CCTV is a pluggable module: only shown once its integration has been added
  // (an explicit cctv module row set to true) AND the user has cctv.view.
  const canSeeCctv            = usePermission("cctv.view") && modules?.cctv === true;
  const canSeeOwnMonitoring   = usePermission("monitor.self.view") && isModuleEnabled("tracker");
  const canSeeMonitor         = useAnyPermission(
    "monitor.view", "monitor.live", "monitor.delete"
  );
  const canSeeMonitorReports  = usePermission("reports.view");
  const canSeeReports         = usePermission("reports.view") && isModuleEnabled("reports");
  const canSeeSettings        = useAnyPermission(
    "settings.view", "settings.write",
    "settings.leave.write", "settings.monitor.write",
    "permissions.write", "holidays.write",
    "tickets.mailboxes.manage", "tickets.mailboxes.test",
    "tickets.mailboxes.oauth", "tickets.email_logs.view"
  );
  const canEditPermissions    = usePermission("permissions.write");
  const canSeeEmailTemplates  = useAnyPermission(
    "email_templates.view", "email_templates.write"
  );
  const canSeeTicketMailboxes = useAnyPermission(
    "tickets.mailboxes.manage", "tickets.mailboxes.test",
    "tickets.mailboxes.oauth", "tickets.email_logs.view"
  );
  // System Admin module — any sub-perm grants the top-level entry; the
  // page itself tab-gates further via @PreAuthorize on each endpoint.
  const canSeeSystem          = useAnyPermission(
    "system.view", "system.audit", "system.sessions",
    "system.tokens", "system.jobs", "system.export"
  ) && isModuleEnabled("system");
  // Assets module — any assets.* perm shows the entry; per-action
  // buttons inside (create, assign, dispose) hide themselves based on
  // narrower perms.  Employees without any assets.* perm still see
  // their own assigned items via /api/assets/mine in their Dashboard,
  // not from this nav entry.
  const hasAssetsPermission   = useAnyPermission(
    "assets.view", "assets.write", "assets.assign", "assets.delete"
  );
  const canSeeAssets          = hasAssetsPermission && isModuleEnabled("assets");
  const hasDocumentsPermission = useAnyPermission(
    "documents.view", "documents.write", "documents.delete"
  );
  const canSeeDocuments       = hasDocumentsPermission && isModuleEnabled("documents");
  const hasBackupPermission   = useAnyPermission("backup.view", "backup.write");
  const canSeeBackups         = hasBackupPermission && isModuleEnabled("backup");
  const hasProjectsPermission = useAnyPermission("projects.view","projects.view_own", "projects.write", "projects.delete");
  const canSeeProjects        = hasProjectsPermission && isModuleEnabled("projects");
  const hasTicketsPermission = useAnyPermission(
    "tickets.view_own", "tickets.view_department", "tickets.view_all",
    "tickets.create", "tickets.reply", "tickets.assign"
  );
  const canSeeTickets = hasTicketsPermission && isModuleEnabled("tickets");
  const brandInitial = (branding.companyName || "P").trim().charAt(0).toUpperCase();
  const brandGradient = `linear-gradient(135deg, ${branding.primaryColor}, ${branding.accentColor})`;
  const brandTextGradient = `linear-gradient(90deg, ${branding.primaryColor}, ${branding.accentColor})`;

  // Auto-open the group containing the active route
  const onMonitoringRoute = location.pathname.startsWith("/admin/monitoring");
  const onSettingsRoute = location.pathname.startsWith("/admin/settings");

  const [openMonitoring, setOpenMonitoring] = useState(onMonitoringRoute);
  const [openSettings, setOpenSettings] = useState(onSettingsRoute);

  // Keep expansion in sync when the route itself changes
  useEffect(() => {
    if (onMonitoringRoute) setOpenMonitoring(true);
  }, [onMonitoringRoute]);
  useEffect(() => {
    if (onSettingsRoute) setOpenSettings(true);
  }, [onSettingsRoute]);

  // Collapsed rail can't show open submenus — force-close them
  useEffect(() => {
    if (collapsed && !isMobile) {
      setOpenMonitoring(false);
      setOpenSettings(false);
    }
  }, [collapsed, isMobile]);

  // Close mobile drawer on navigation
  useEffect(() => {
    if (isMobile && mobileOpen) setMobileOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Escape key closes mobile drawer
  useEffect(() => {
    if (!isMobile || !mobileOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isMobile, mobileOpen, setMobileOpen]);

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (!isMobile) return;
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isMobile, mobileOpen]);

  // Effective geometry
  const shellWidthClass = isMobile
    ? WIDTH_MOBILE
    : collapsed
    ? WIDTH_COLLAPSED
    : WIDTH_EXPANDED;

  const labelsHidden = !isMobile && collapsed;

  // Translate-off-canvas on mobile when closed
  const mobileTransform = isMobile
    ? mobileOpen
      ? "translate-x-0"
      : "-translate-x-full"
    : "translate-x-0";

  return (
    <>
      {/* ---------- BACKDROP (mobile only) ---------- */}
      <div
        onClick={() => setMobileOpen(false)}
        aria-hidden
        className={`fixed inset-0 z-30 bg-black/50 dark:bg-black/70 backdrop-blur-sm md:hidden transition-opacity duration-300 ${
          isMobile && mobileOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      />

      {/* ---------- SIDEBAR SHELL ---------- */}
      <aside
        aria-label="Primary navigation"
        className={[
          // Position: fixed on mobile (drawer) and desktop (rail)
          "fixed top-0 left-0 z-40 h-screen",
          "flex flex-col",
          "bg-white text-gray-800 shadow-xl",
          "dark:bg-slate-900 dark:text-gray-100 dark:shadow-black/40",
          // Subtle right border
          "border-r border-gray-100 dark:border-slate-800",
          // Smooth width + transform transitions
          "transition-[width,transform,background-color] duration-300 ease-in-out",
          shellWidthClass,
          mobileTransform,
        ].join(" ")}
      >
        {/* ---------- HEADER ---------- */}
        <div
          className={[
            "flex items-center h-16 border-b border-gray-100 dark:border-slate-800 shrink-0",
            labelsHidden ? "justify-center px-2" : "justify-between px-4",
          ].join(" ")}
        >
          {labelsHidden ? (
            // Collapsed desktop rail — only the logo, and it doubles as the expand button
            <button
              type="button"
              onClick={() => setCollapsed(false)}
              aria-label="Expand sidebar"
              style={branding.logoUrl ? undefined : { background: brandGradient }}
              className="group relative w-10 h-10 rounded-xl text-white flex items-center justify-center font-bold shadow-md shrink-0 overflow-hidden transition-transform hover:scale-105 active:scale-95"
            >
              {branding.logoUrl ? (
                <img
                  src={branding.logoUrl}
                  alt={branding.companyName}
                  loading="eager"
                  decoding="async"
                  className="w-full h-full object-contain bg-white"
                />
              ) : (
                brandInitial
              )}
              <Tooltip>Expand sidebar</Tooltip>
            </button>
          ) : (
            <>
              <div className="flex items-center gap-3 overflow-hidden min-w-0">
                <div
                  style={branding.logoUrl ? undefined : { background: brandGradient }}
                  className="w-9 h-9 rounded-xl text-white flex items-center justify-center font-bold shadow-md shrink-0 overflow-hidden transition-transform hover:scale-105"
                >
                  {branding.logoUrl ? (
                    <img
                      src={branding.logoUrl}
                      alt={branding.companyName}
                      loading="eager"
                      decoding="async"
                      className="w-full h-full object-contain bg-white"
                    />
                  ) : (
                    brandInitial
                  )}
                </div>

                <span
                  className="text-lg font-bold bg-clip-text text-transparent whitespace-nowrap truncate"
                  style={{ backgroundImage: brandTextGradient }}
                >
                  {branding.companyName || "Admin"}
                </span>
              </div>

              {/* Mobile close / Desktop collapse toggle */}
              {isMobile ? (
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  aria-label="Close menu"
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 active:scale-95 transition shrink-0"
                >
                  <X size={18} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setCollapsed(true)}
                  aria-label="Collapse sidebar"
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 active:scale-95 transition shrink-0"
                >
                  <Menu size={18} />
                </button>
              )}
            </>
          )}
        </div>

        {/* ---------- NAV ---------- */}
        <nav
          ref={scrollerRef}
          className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 space-y-1 scroll-smooth [scrollbar-width:thin]"
        >
          {/* Dashboard, Attendance and Leaves are universal — every authenticated
              user has the matching self-service perm, so no gate needed. */}
          <NavItem to="/admin" end Icon={LayoutDashboard} label="Dashboard" collapsed={labelsHidden} />
          {canSeeUsers && (
            <NavItem to="/admin/employees" Icon={Users} label="Employees" collapsed={labelsHidden} />
          )}
          {canSeeUsers && (
            <NavItem to="/admin/departments" Icon={Building2} label="Departments" collapsed={labelsHidden} />
          )}
          {canSeePayroll && (
            <NavItem to="/admin/payroll" Icon={Wallet} label="Payroll" collapsed={labelsHidden} />
          )}
          {isModuleEnabled("attendance") && <NavItem to="/admin/attendance" Icon={Clock} label="Attendance" collapsed={labelsHidden} />}
          {canSeeShifts && (
            <NavItem to="/admin/shifts" Icon={CalendarClock} label="Shifts & Roster" collapsed={labelsHidden} />
          )}
          {canSeeMobileReleases && (
            <NavItem to="/admin/mobile-releases" Icon={Smartphone} label="Mobile Releases" collapsed={labelsHidden} />
          )}
          {canSeeOwnMonitoring && (
            <NavItem to="/admin/my-monitoring-hours" Icon={TrendingUp} label="My Monitoring Hours" collapsed={labelsHidden} />
          )}
          {isModuleEnabled("leaves") && <NavItem to="/admin/leaves" Icon={Calendar} label="Leaves" collapsed={labelsHidden} />}
          {canSeeLeaveBalances && (
            <NavItem to="/admin/leave-balances" Icon={Wallet} label="Leave Balances" collapsed={labelsHidden} />
          )}
          {canSeeClients && (
            <NavItem to="/admin/clients" Icon={Building2} label="Clients" collapsed={labelsHidden} />
          )}
          {canSeeTravel && (
            <NavItem to="/admin/travel-logs" Icon={Navigation} label="Travel Logs" collapsed={labelsHidden} />
          )}
          {canSeeTravel && (
            <NavItem to="/admin/live-travel" Icon={RadioTower} label="Live Travel" collapsed={labelsHidden} />
          )}
          {canSeeReimbursements && (
            <NavItem to="/admin/reimbursements" Icon={ReceiptText} label="Expense Claims" collapsed={labelsHidden} />
          )}
          {canSeeAnnouncements && (
            <NavItem to="/admin/announcements" Icon={Megaphone} label="Announcements" collapsed={labelsHidden} />
          )}
          {canSeeFeedback && (
            <NavItem to="/admin/feedback" Icon={MessageSquare} label="Customer Feedback" collapsed={labelsHidden} />
          )}
          {canSeeCertificates && (
            <NavItem to="/admin/certificates" Icon={Award} label="Certificates" collapsed={labelsHidden} />
          )}
          {canSeeCctv && (
            <NavItem to="/cctv" Icon={Cctv} label="CCTV" collapsed={labelsHidden} />
          )}
          {canSeeAssets && (
            <NavItem to="/admin/assets" Icon={Package} label="Assets" collapsed={labelsHidden} />
          )}
          {canSeeDocuments && (
            <NavItem to="/admin/documents" Icon={FileText} label="Documents" collapsed={labelsHidden} />
          )}
          {canSeeBackups && (
            <NavItem to="/admin/backups" Icon={HardDrive} label="Laptop Backups" collapsed={labelsHidden} />
          )}
          {canSeeProjects && (
            <NavItem to="/admin/projects" Icon={Briefcase} label="Projects & Tasks" collapsed={labelsHidden} />
          )}
          {canSeeTickets && (
            <NavItem to="/admin/tickets" Icon={LifeBuoy} label="Tickets" collapsed={labelsHidden} />
          )}
          {canSeeReports && (
            <NavItem to="/admin/reports" Icon={BarChart3} label="Reports" collapsed={labelsHidden} />
          )}
          {canSeeSystem && (
            <NavItem to="/admin/system" Icon={ShieldCheck} label="System Admin" collapsed={labelsHidden} />
          )}

          {/* ---------- MONITORING GROUP ----------
              The whole accordion is gated on monitor.view (or live/delete) —
              if the caller can't see any of it, we hide the heading too so the
              collapsed rail doesn't show an icon that opens to nothing. The
              Reports sub-row has its own narrower gate. */}
          {canSeeMonitor && (
            <Accordion
              open={openMonitoring && !labelsHidden}
              onToggle={() => {
                if (labelsHidden) {
                  // Tapping an icon in the collapsed rail auto-expands the sidebar
                  setCollapsed(false);
                  setOpenMonitoring(true);
                } else {
                  setOpenMonitoring((o) => !o);
                }
              }}
              Icon={Monitor}
              label="Monitoring"
              collapsed={labelsHidden}
              active={onMonitoringRoute}
            >
              <SubNav to="/admin/monitoring/dashboard" Icon={LayoutDashboard} label="Dashboard" />
              <SubNav to="/admin/monitoring/live-screens" Icon={Tv} label="Live Screens" />
              <SubNav to="/admin/monitoring/screenshots" Icon={ImageIcon} label="Screenshots" />
              <SubNav to="/admin/monitoring/productivity" Icon={TrendingUp} label="Productivity" />
              <SubNav to="/admin/monitoring/activities" Icon={ActivityIcon} label="Activities" />
              <SubNav to="/admin/monitoring/shutdown-logs" Icon={Power} label="Shutdown Logs" />
              <SubNav to="/admin/monitoring/keystrokes" Icon={Keyboard} label="Keystrokes" />
              <SubNav to="/admin/monitoring/users" Icon={Users} label="Users" />
              {canSeeMonitorReports && (
                <SubNav to="/admin/monitoring/reports" Icon={BarChart3} label="Reports" />
              )}
            </Accordion>
          )}

          {/* ---------- SETTINGS GROUP ----------
              Hide the whole group from anyone with no settings permissions at
              all (a plain EMPLOYEE). Sub-items live behind the same gate at
              the route level too — the sidebar gate is purely cosmetic. The
              Permissions sub-row has the narrowest gate (permissions.write,
              ADMIN-only by default). */}
          {canSeeSettings && (
            <Accordion
              open={openSettings && !labelsHidden}
              onToggle={() => {
                if (labelsHidden) {
                  setCollapsed(false);
                  setOpenSettings(true);
                } else {
                  setOpenSettings((o) => !o);
                }
              }}
              Icon={Settings}
              label="Settings"
              collapsed={labelsHidden}
              active={onSettingsRoute}
            >
              {isModuleEnabled("attendance") && (
                <SubNav to="/admin/settings/attendance" Icon={Clock} label="Attendance" />
              )}
              {isModuleEnabled("leaves") && (
                <SubNav to="/admin/settings/leave-policy" Icon={Calendar} label="Leave Policy" />
              )}
              {isModuleEnabled("tracker") && (
                <SubNav to="/admin/settings/monitoring" Icon={Monitor} label="Monitoring" />
              )}
              <SubNav to="/admin/settings/notifications" Icon={Bell}         label="Notifications" />
              <SubNav to="/admin/settings/company"       Icon={Building2}    label="Company" />
              <SubNav to="/admin/settings/security"      Icon={Shield}       label="Security" />
              <SubNav to="/admin/settings/maintenance"   Icon={Shield}       label="Maintenance Mode" />
              {canEditPermissions && (
                <SubNav to="/admin/settings/permissions"   Icon={ShieldCheck}  label="Permissions" />
              )}
              {isModuleEnabled("leaves") && (
                <SubNav to="/admin/settings/holidays" Icon={CalendarDays} label="Holidays" />
              )}
              <SubNav to="/admin/settings/integrations"  Icon={Plug}         label="Integrations" />
              <SubNav to="/admin/settings/api-integrations" Icon={Cctv}      label="API Modules" />
              {canSeeEmailTemplates && (
                <SubNav to="/admin/settings/email-templates" Icon={Mail}     label="Email Templates" />
              )}
              {canSeeTicketMailboxes && isModuleEnabled("tickets") && (
                <SubNav to="/admin/settings/ticket-mailboxes" Icon={Mail} label="Ticket Mailboxes" />
              )}
              {canEditPermissions && (
                <SubNav to="/admin/settings/modules" Icon={Plug} label="Module Settings" />
              )}
            </Accordion>
          )}
        </nav>

        {/* ---------- FOOTER / LOGOUT ---------- */}
        <div className="shrink-0 border-t border-gray-100 dark:border-slate-800 p-3">
          <button
            type="button"
            onClick={onLogout}
            className="group relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-700 dark:hover:text-red-300 active:scale-[0.98] transition-all"
          >
            <LogOut size={20} className="shrink-0 transition-transform group-hover:-translate-x-0.5" />
            <Label hidden={labelsHidden}>
              <span className="text-sm font-medium whitespace-nowrap">Logout</span>
            </Label>

            {labelsHidden && <Tooltip>Logout</Tooltip>}
          </button>
        </div>
      </aside>
    </>
  );
}

/* ------------------------------------------------------------------------ */
/*                          NAV SUBCOMPONENTS                               */
/* ------------------------------------------------------------------------ */

/** Single top-level item with active indicator + tooltip-on-collapsed. */
function NavItem({ to, end, Icon, label, collapsed }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        [
          "group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium",
          "transition-all duration-200 active:scale-[0.98]",
          isActive
            ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md shadow-blue-500/30"
            : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white",
        ].join(" ")
      }
    >
      {({ isActive }) => (
        <>
          {/* Left indicator bar that slides in on active */}
          <span
            aria-hidden
            className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 rounded-r-full bg-white transition-all duration-300 ${
              isActive ? "h-6 opacity-100" : "h-0 opacity-0"
            }`}
          />
          <Icon
            size={20}
            className={`shrink-0 transition-transform ${
              isActive ? "scale-110" : "group-hover:scale-110"
            }`}
          />
          <Label hidden={collapsed}>
            <span className="whitespace-nowrap">{label}</span>
          </Label>

          {collapsed && <Tooltip>{label}</Tooltip>}
        </>
      )}
    </NavLink>
  );
}

/** Child nav item inside an Accordion. */
function SubNav({ to, Icon, label }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        [
          "group flex items-center gap-3 pl-4 pr-3 py-2 rounded-lg text-sm",
          "transition-all duration-200",
          isActive
            ? "bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 font-medium"
            : "text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-800 hover:text-gray-800 dark:hover:text-gray-100",
        ].join(" ")
      }
    >
      {({ isActive }) => (
        <>
          <span
            aria-hidden
            className={`w-1.5 h-1.5 rounded-full transition-all ${
              isActive ? "bg-blue-500 scale-110" : "bg-gray-300 dark:bg-slate-600 group-hover:bg-gray-400 dark:group-hover:bg-slate-500"
            }`}
          />
          <Icon size={16} className="shrink-0" />
          <span className="whitespace-nowrap">{label}</span>
        </>
      )}
    </NavLink>
  );
}

/** Expanding accordion section with smooth height animation (grid-rows trick). */
function Accordion({ open, onToggle, Icon, label, collapsed, active, children }) {
  const id = useMemo(
    () => `acc-${label.toLowerCase().replace(/\s+/g, "-")}`,
    [label]
  );

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={id}
        className={[
          "group relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium",
          "transition-all duration-200 active:scale-[0.98]",
          active
            ? "text-blue-700 dark:text-blue-300 bg-blue-50/60 dark:bg-blue-500/10"
            : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white",
        ].join(" ")}
      >
        <Icon
          size={20}
          className={`shrink-0 transition-transform ${
            active ? "scale-110" : "group-hover:scale-110"
          }`}
        />
        <Label hidden={collapsed}>
          <span className="flex-1 text-left whitespace-nowrap">{label}</span>
        </Label>

        {!collapsed && (
          <ChevronRight
            size={16}
            className={`shrink-0 transition-transform duration-300 ${
              open ? "rotate-90" : ""
            }`}
          />
        )}

        {collapsed && <Tooltip>{label}</Tooltip>}
      </button>

      {/* grid-rows 0fr ↔ 1fr gives pure-CSS height animation */}
      <div
        id={id}
        className={`grid transition-all duration-300 ease-in-out ${
          open ? "grid-rows-[1fr] opacity-100 mt-1" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <div className="ml-4 pl-3 border-l border-gray-200 dark:border-slate-700 space-y-1 py-1">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Fade + slide wrapper for labels when the rail collapses. */
function Label({ hidden, children }) {
  return (
    <span
      className={[
        "overflow-hidden transition-all duration-200",
        hidden
          ? "opacity-0 -translate-x-2 w-0"
          : "opacity-100 translate-x-0 w-auto",
      ].join(" ")}
      aria-hidden={hidden || undefined}
    >
      {children}
    </span>
  );
}

/** Absolute-positioned tooltip that shows on hover when the rail is collapsed. */
function Tooltip({ children }) {
  return (
    <span
      role="tooltip"
      className={[
        "pointer-events-none absolute left-full ml-3 top-1/2 -translate-y-1/2 z-50",
        "px-2.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap",
        "bg-gray-900 text-white shadow-lg",
        "opacity-0 translate-x-[-4px] group-hover:opacity-100 group-hover:translate-x-0",
        "transition-all duration-150",
        // Little arrow
        "before:content-[''] before:absolute before:right-full before:top-1/2",
        "before:-translate-y-1/2 before:border-4 before:border-transparent before:border-r-gray-900",
      ].join(" ")}
    >
      {children}
    </span>
  );
}
